/**
 * CreateTripDialog - Dialog for creating a new trip.
 *
 * Opens when user clicks on the globe surface or the "New Trip" button.
 * Collects title, description, dates, and optional city information.
 * Uses glass morphism styling to blend with the globe background.
 */

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useCreateTrip } from '@/hooks/trip'
import { useCreateDestination } from '@/hooks/destination'
import { FONT_STYLES } from '@/styles/fonts'

interface CreateTripDialogProps {
  /** Location where the trip was initiated (from globe click) */
  location: { lat: number; lng: number }
  /** Called when dialog is closed */
  onClose: () => void
  /** Called when trip is successfully created with the new trip ID */
  onCreated: (tripId: string) => void
}

/**
 * Dialog for creating a new trip.
 *
 * @example
 * ```tsx
 * {createLocation && (
 *   <CreateTripDialog
 *     location={createLocation}
 *     onClose={() => setCreateLocation(null)}
 *     onCreated={(tripId) => {
 *       setCreateLocation(null)
 *       selectTrip(tripId)
 *     }}
 *   />
 * )}
 * ```
 */
export function CreateTripDialog({
  location,
  onClose,
  onCreated,
}: CreateTripDialogProps) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [departureCity, setDepartureCity] = useState('')
  const [arrivalCity, setArrivalCity] = useState('')
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({})
  const [formError, setFormError] = useState<string | null>(null)

  const { createTrip, loading: tripLoading } = useCreateTrip()
  const { createDestination, loading: destLoading } = useCreateDestination()

  const loading = tripLoading || destLoading

  const resetForm = () => {
    setTitle('')
    setDescription('')
    setDepartureCity('')
    setArrivalCity('')
    setDateRange({})
    setFormError(null)
  }

  const handleSubmit = async () => {
    // Validate title
    if (!title.trim()) {
      setFormError('Title is required')
      return
    }

    // Validate date range
    if (dateRange.from && dateRange.to && dateRange.from > dateRange.to) {
      setFormError('Return date must be after departure date')
      return
    }

    try {
      const response = await createTrip(
        title,
        description || 'A new adventure awaits!',
        dateRange.from && dateRange.to
          ? {
              departure: {
                year: dateRange.from.getFullYear(),
                month: dateRange.from.getMonth() + 1,
                day: dateRange.from.getDate(),
              },
              return: {
                year: dateRange.to.getFullYear(),
                month: dateRange.to.getMonth() + 1,
                day: dateRange.to.getDate(),
              },
            }
          : undefined
      )

      const tripId = response.trip?.tripId
      if (!tripId) {
        setFormError('Failed to create trip')
        return
      }

      // Create initial destination if arrivalCity is provided
      if (arrivalCity.trim()) {
        try {
          await createDestination(tripId, {
            location: arrivalCity,
            viewport: location
              ? { latitude: location.lat, longitude: location.lng }
              : undefined,
          })
        } catch (destError) {
          // Log but don't block - trip was created successfully
          console.warn('Failed to create initial destination:', destError)
        }
      }

      resetForm()
      onCreated(tripId)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create trip')
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-black/40 backdrop-blur-sm border-2 border-white/30 rounded-lg px-6 pt-6 pb-6 gap-6 max-w-md">
        <DialogHeader>
          <DialogTitle
            className="text-2xl font-bold text-white text-center tracking-wide"
            style={FONT_STYLES.pioneer}
          >
            NEW TRIP
          </DialogTitle>
          <DialogDescription
            className="text-center text-sm text-white/70 mt-2"
            style={FONT_STYLES.alga}
          >
            Plan your next adventure
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Trip Name */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider font-semibold mb-2 text-white/60"
              style={FONT_STYLES.alga}
            >
              Trip Name
            </label>
            <Input
              placeholder="My Amazing Trip"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-white/10 border-white/30 text-white placeholder:text-white/40 focus:border-white/50 rounded-md"
              style={FONT_STYLES.alga}
            />
          </div>

          {/* Cities Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Departure City */}
            <div>
              <label
                className="block text-xs uppercase tracking-wider font-semibold mb-2 text-white/60"
                style={FONT_STYLES.alga}
              >
                From
              </label>
              <Input
                placeholder="San Francisco"
                value={departureCity}
                onChange={(e) => setDepartureCity(e.target.value)}
                className="w-full bg-white/10 border-white/30 text-white placeholder:text-white/40 focus:border-white/50 rounded-md"
                style={FONT_STYLES.alga}
              />
            </div>

            {/* Arrival City */}
            <div>
              <label
                className="block text-xs uppercase tracking-wider font-semibold mb-2 text-white/60"
                style={FONT_STYLES.alga}
              >
                To
              </label>
              <Input
                placeholder="New York"
                value={arrivalCity}
                onChange={(e) => setArrivalCity(e.target.value)}
                className="w-full bg-white/10 border-white/30 text-white placeholder:text-white/40 focus:border-white/50 rounded-md"
                style={FONT_STYLES.alga}
              />
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider font-semibold mb-2 text-white/60"
              style={FONT_STYLES.alga}
            >
              Dates
            </label>
            <div className="flex items-center gap-3">
              <input
                type="date"
                value={dateRange.from ? dateRange.from.toISOString().slice(0, 10) : ''}
                onChange={(e) => {
                  const val = e.target.value
                    ? new Date(e.target.value + 'T00:00:00')
                    : undefined
                  setDateRange((prev) => ({ ...prev, from: val }))
                }}
                className="flex-1 bg-white/10 border border-white/30 text-white rounded-md px-3 py-2 focus:border-white/50 focus:outline-none"
                style={FONT_STYLES.alga}
              />
              <span className="text-white/40">→</span>
              <input
                type="date"
                value={dateRange.to ? dateRange.to.toISOString().slice(0, 10) : ''}
                onChange={(e) => {
                  const val = e.target.value
                    ? new Date(e.target.value + 'T00:00:00')
                    : undefined
                  setDateRange((prev) => ({ ...prev, to: val }))
                }}
                className="flex-1 bg-white/10 border border-white/30 text-white rounded-md px-3 py-2 focus:border-white/50 focus:outline-none"
                style={FONT_STYLES.alga}
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider font-semibold mb-2 text-white/60"
              style={FONT_STYLES.alga}
            >
              Description (Optional)
            </label>
            <Textarea
              placeholder="What makes this trip special..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-white/10 border-white/30 text-white placeholder:text-white/40 focus:border-white/50 resize-none rounded-md"
              style={FONT_STYLES.alga}
              rows={3}
            />
          </div>

          {formError && (
            <p className="text-sm text-red-400 text-center">{formError}</p>
          )}
        </div>

        <DialogFooter>
          <div className="flex gap-3 w-full">
            <Button
              onClick={onClose}
              variant="ghost"
              className="flex-1 bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-md"
              style={FONT_STYLES.alga}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-white/90 hover:bg-white text-gray-900 rounded-md font-bold"
              style={FONT_STYLES.pioneer}
            >
              {loading ? 'CREATING...' : 'CREATE TRIP'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
